import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslation from './locales/en.json';
import frTranslation from './locales/fr.json';

const supportedLanguages = ['en', 'fr'] as const;
const supportedLngs = [...supportedLanguages];

type SupportedLanguage = (typeof supportedLanguages)[number];

const normalizeLanguage = (language?: string | null): SupportedLanguage | null => {
  if (!language) {
    return null;
  }

  const lower = language.toLowerCase();
  if (supportedLanguages.includes(lower as SupportedLanguage)) {
    return lower as SupportedLanguage;
  }

  const base = lower.split('-')[0];
  if (supportedLanguages.includes(base as SupportedLanguage)) {
    return base as SupportedLanguage;
  }

  return null;
};

if (typeof window !== 'undefined') {
  const storedLanguage = localStorage.getItem('i18nextLng');
  const normalizedStored = normalizeLanguage(storedLanguage);
  if (normalizedStored) {
    localStorage.setItem('i18nextLng', normalizedStored);
  } else {
    const navigatorLanguage =
      normalizeLanguage(navigator.language) ||
      normalizeLanguage(navigator.languages?.[0]) ||
      'en';
    localStorage.setItem('i18nextLng', navigatorLanguage);
  }
}

const resources = {
  en: {
    translation: enTranslation
  },
  fr: {
    translation: frTranslation
  }
};

i18n
  // detect user language
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next
  .use(initReactI18next)
  // init i18next
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    supportedLngs,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    lowerCaseLng: true,
    react: {
      useSuspense: false // Prevents issues during development
    }
  });

export default i18n;
